import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { CardPreviewPane } from '../CardHoverPreview/CardHoverPreview';
import './ListingCard.css';

const gradeDigits = (grade) => {
    const m = String(grade || '').match(/\d+(\.\d+)?/);
    return m ? m[0] : null;
};

const isMobileView = () => typeof window !== 'undefined' && window.innerWidth <= 600;

// Portrait listing card: cert image + separate price tag (name and PSA grade on one line). On
// desktop hover it shows the enlarged cert preview (same as the listings pages)
// and a cursor-following flavor-text tooltip that types out.
const ListingCard = ({ listing }) => {
    const cert = listing?.cert || {};
    const img = cert.cert_images?.front_image_url;
    // Prefer the card's clean name over the cert's label text.
    const name = cert.card?.name || cert.name || `Cert ${cert.cert_number}`;
    const setName = cert.card?.set?.name || cert.set || '';
    const grade = gradeDigits(cert.grade);
    const price = Number(listing?.price);
    const flavorText = cert.card?.flavor_text;

    const [hovered, setHovered] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [typed, setTyped] = useState('');
    const typingRef = useRef(null);

    useEffect(() => () => {
        if (typingRef.current) clearInterval(typingRef.current);
    }, []);

    const startTyping = () => {
        if (!flavorText) return;
        setTyped('');
        if (typingRef.current) clearInterval(typingRef.current);
        let i = 0;
        typingRef.current = setInterval(() => {
            setTyped((prev) => prev + flavorText[i]);
            i += 1;
            if (i >= flavorText.length) clearInterval(typingRef.current);
        }, 30);
    };

    // Real mouse only. Touch screens fire fake hover events on tap, which would
    // open this desktop tooltip and leave it typing after the finger lifts.
    // Phones get no flavor text, so nothing interferes with tapping a card.
    const isMouse = (e) => e.pointerType === 'mouse' && !isMobileView();

    const onEnter = (e) => {
        if (!isMouse(e)) return;
        setHovered(true);
        startTyping();
    };

    const onLeave = () => {
        setHovered(false);
        setTyped('');
        if (typingRef.current) clearInterval(typingRef.current);
    };

    const onMove = (e) => {
        if (!isMouse(e)) return;
        setMousePos({ x: e.clientX, y: e.clientY });
    };

    // Touch screens: lift the card the instant a finger lands on it, keep it
    // lifted while the finger stays down (including while scrolling), and drop
    // it as soon as the finger lifts. Toggled straight on the element, so there's
    // no re-render to slow a tap down; a tap still opens the listing as normal.
    const press = (e) => e.currentTarget.classList.add('is-pressed');
    const release = (e) => e.currentTarget.classList.remove('is-pressed');

    return (
        <Link
            to={`/listing/${cert.cert_number}`}
            className="gh-lcard"
            onPointerEnter={onEnter}
            onPointerLeave={onLeave}
            onPointerMove={onMove}
            onTouchStart={press}
            onTouchEnd={release}
            onTouchCancel={release}
        >
            <div className="gh-lcard-media">
                {img ? (
                    <img src={img} alt={name} loading="lazy" />
                ) : (
                    <span className="gh-lcard-noimg" aria-hidden="true">—</span>
                )}
            </div>
            <div className="gh-lcard-tag">
                <div className="gh-lcard-text">
                    <div className="gh-lcard-name-row">
                        <span className="gh-lcard-name">{name}</span>
                        {grade && <strong className="gh-lcard-grade">PSA {grade}</strong>}
                    </div>
                    {setName && <span className="gh-lcard-set">{setName}</span>}
                    {Number.isFinite(price) && <span className="gh-lcard-price">${price.toFixed(2)}</span>}
                </div>
                <span className="gh-lcard-go" aria-hidden="true">›</span>
            </div>

            {/* Enlarged cert preview on hover — same pane the listings pages use. */}
            {hovered && img && !isMobileView() && (
                <CardPreviewPane src={img} alt={name} mousePos={mousePos} />
            )}

            {/* Typewriter flavor-text tooltip near the cursor. */}
            {hovered && flavorText && !isMobileView() && createPortal(
                <div
                    className="gh-lcard-flavor"
                    style={{ position: 'fixed', left: mousePos.x + 20, top: mousePos.y + 20, zIndex: 9999 }}
                >
                    <p><i>{typed}</i></p>
                </div>,
                document.body
            )}
        </Link>
    );
};

export default ListingCard;
